"use client";

import type { ReactNode } from "react";
import { useEffect, useRef } from "react";

import Image from "next/image";

import bannerBgDesktop from "@repo/ui/assets/images/home-banner-bg-desktop.png";
import bannerBgMobile from "@repo/ui/assets/images/home-banner-bg-mobile.png";
import { HeroCarousel } from "@repo/ui/components";
import { usePageContext } from "@repo/ui/contexts/PageContext";
import type { HomePageContent } from "@repo/ui/types/cms/home";

import type { NormalizedHomePageSections } from "@/features/home/helpers/normalizeHomePageSections";

// Wraps the box image in a link when a CTA link exists; plain div otherwise.
function BoxLink({
  href,
  ariaLabel,
  className,
  children,
}: Readonly<{ href: string; ariaLabel: string; className: string; children: ReactNode }>) {
  if (!href) {
    return <div className={className}>{children}</div>;
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      aria-label={ariaLabel}
    >
      {children}
    </a>
  );
}

export default function Carousel() {
  // ⚙️ CMS content
  const { normalizedSections } = usePageContext<HomePageContent, NormalizedHomePageSections>();
  const { bannerCarousel } = normalizedSections;

  // Publishes the box's real height as a CSS var (mirrors --home-widget-h), so slide
  // min-height never hardcodes a guess. Measured off slide 1 only — all slides match.
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;

    const publishHeight = () => {
      document.documentElement.style.setProperty("--banner-box-h", `${el.offsetHeight}px`);
    };

    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (!bannerCarousel?.carouselItems) {
    return null;
  }

  const slides = bannerCarousel.carouselItems.map((item, index) => {
    const boxImageAlt = item.ctaText || `Banner ${index + 1}`;

    return {
      id: index + 1,
      content: (
        <div className="relative block h-full w-full overflow-hidden">
          <div className="absolute inset-0 z-0">
            <Image
              src={item.backgroundImageUrl || bannerBgDesktop}
              alt={item.ctaText || `Banner ${index + 1}`}
              fill
              priority={index === 0}
              fetchPriority={index === 0 ? "high" : "auto"}
              loading={index === 0 ? "eager" : "lazy"}
              quality={70}
              sizes="(max-width: 1440px) 100vw, 1440px"
              className="object-cover object-top hidden md:block"
            />
            <Image
              src={item.mobileBackgroundImageUrl || bannerBgMobile}
              alt={item.ctaText || `Banner ${index + 1}`}
              fill
              priority={index === 0}
              fetchPriority={index === 0 ? "high" : "auto"}
              loading={index === 0 ? "eager" : "lazy"}
              quality={70}
              sizes="(max-width: 768px) 100vw"
              className="object-cover object-bottom md:hidden"
            />
          </div>

          {(item.title || item.subtitle) && (
            <div className="relative z-10 hidden h-full flex-col justify-center gap-1 px-6 text-white md:flex md:px-12">
              {item.title && <h2 className="text-2xl font-bold md:text-4xl">{item.title}</h2>}
              {item.subtitle && <p className="text-sm md:text-lg">{item.subtitle}</p>}
            </div>
          )}

          {/* Box renders only when CMS provides one for that device — no local fallback,
              so a background-only slide shows the background cleanly instead of an
              unrelated placeholder graphic overlaid on top. */}
          {item.boxImageUrl && (
            <div className="absolute inset-4 z-10 hidden items-center justify-center md:flex">
              <BoxLink
                href={item.ctaLink}
                ariaLabel={boxImageAlt}
                className="relative aspect-[1400/290] w-full max-w-[1400px]"
              >
                <Image
                  src={item.boxImageUrl}
                  alt={boxImageAlt}
                  fill
                  quality={90}
                  sizes="(max-width: 768px) 100vw, 1400px"
                  className="object-contain"
                />
              </BoxLink>
            </div>
          )}
          {/* Positions relative to the live search widget height via --home-widget-h.
              ref only on the first slide's box — see boxRef comment above. */}
          {item.mobileBoxImageUrl && (
            <div
              ref={index === 0 ? boxRef : undefined}
              className="absolute inset-x-2 z-10 md:hidden"
              style={{ top: "calc(var(--home-widget-h, 540px) + 32px)" }}
            >
              <BoxLink
                href={item.ctaLink}
                ariaLabel={boxImageAlt}
                className="relative block aspect-[730/380] w-full"
              >
                <Image
                  src={item.mobileBoxImageUrl}
                  alt={boxImageAlt}
                  fill
                  quality={90}
                  sizes="100vw"
                  className="object-contain"
                />
              </BoxLink>
            </div>
          )}
        </div>
      ),
    };
  });

  return (
    <HeroCarousel
      items={slides}
      autoplayOptions={{ delay: 5000 }}
      // Mobile: widget height + box height (both measured) + 94px fixed gaps/pill. Desktop unchanged.
      slideClassName="min-h-[calc(var(--home-widget-h,540px)+var(--banner-box-h,195px)+94px)] md:aspect-[16/9] md:h-auto md:max-h-[560px] md:min-h-[380px]"
      showDotControls
    />
  );
}
